import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import ComboBox, { ComboBoxOption } from '@ui/ComboBox';
import {
  getCheapestEstimate,
  isDomestic,
  postalCodeToState,
} from '@util/firestore/shipengine';
import { getDefaultAddress } from '@util/firestore/users';
import { formatCurrency } from '@util/index';
import { getCountries } from '@util/types/locations';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import type { CountryCode } from 'libphonenumber-js/types';
import { ProductDocument } from 'models/product';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const estimateShippingSchema = z.object({
  country: z.string(),
  postal_code: z.string().min(4),
});

type EstimateShippingFormData = z.infer<typeof estimateShippingSchema>;

type EstimateShippingResult = {
  cost: number;
  code: string;
  name: string;
  estimated: true;
};

type EstimateShippingFormProps = {
  product: ProductDocument;
  onComplete: (result: EstimateShippingResult | null) => void;
  onDismiss: () => void;
  className?: string;
};

export default function EstimateShippingForm({
  product,
  onComplete,
  onDismiss,
  className = '',
}: EstimateShippingFormProps) {
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState<number | null>(null);
  const {
    handleSubmit,
    control,
    register,
    setValue,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<EstimateShippingFormData>({
    defaultValues: {
      country: 'US',
    },
    resolver: zodResolver(estimateShippingSchema),
  });

  const data = watch();
  const { showErrorToast } = useToastContext();
  const { userDoc } = useAuth();
  const queryClient = useQueryClient();

  const onSubmit = async (form: EstimateShippingFormData) => {
    setLoading(true);
    try {
      if (
        isDomestic(product.shipment, {
          country_code: form.country as CountryCode,
          state_province: postalCodeToState(form.postal_code),
        })
      ) {
        const cost = product.shipment?.domestic_flat_rate ?? 0;
        setShippingCost(cost);
        queryClient.setQueryData(['shipping', product.id, userDoc?.uid ?? ''], {
          cost,
          code: form.country,
          name: form.postal_code,
        });
      } else {
        const cheapest = await getCheapestEstimate(
          product.id,
          form.postal_code,
          form.country
        );
        setShippingCost(cheapest.total_amount);
        queryClient.setQueryData(['shipping', product.id, userDoc?.uid ?? ''], {
          cost: cheapest.total_amount,
          code: form.country,
          name: form.postal_code,
        });
      }
    } catch (e) {
      setError('postal_code', {
        message: 'Shipping is not available for this postal code and country',
      });
      showErrorToast(
        'Shipping is not available for this postal code and country'
      );
    }

    setLoading(false);
  };

  function handleComboBoxChange(optionValue: ComboBoxOption) {
    setValue('country', optionValue.value);
    trigger('country');
  }

  const handleClose = () => {
    const result = shippingCost
      ? {
          cost: shippingCost,
          code: data.country,
          name: data.country,
          estimated: true as const,
        }
      : null;

    onComplete(result);
    onDismiss();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`flex h-full flex-col justify-between ${className}`}
    >
      <div className="flex flex-col gap-6">
        {shippingCost === null ? (
          <>
            <FormLabel
              value="Country"
              required={true}
              className="!text-[1.8rem]"
            >
              <Controller
                rules={{ required: true }}
                control={control}
                name="country"
                render={() => (
                  <ComboBox
                    defaultValue={
                      getDefaultAddress(userDoc)?.country_code ?? 'US'
                    }
                    autoComplete="country-name"
                    allowEmpty={false}
                    options={getCountries()}
                    error={!!errors.country}
                    onChange={handleComboBoxChange}
                    className="!text-[1.8rem]"
                  />
                )}
              />
            </FormLabel>
            <FormLabel
              value="Postal Code"
              errorMessage={errors.postal_code?.message}
              className="!text-[1.8rem]"
            >
              <FormInput
                autoComplete="postal-code"
                placeholder="90045"
                defaultValue={getDefaultAddress(userDoc)?.postal_code ?? ''}
                error={!!errors.country}
                {...register('postal_code')}
                className="h-[3.5rem] !text-[1.8rem]"
              />
            </FormLabel>
          </>
        ) : (
          <div className="my-[5rem] text-center">
            <h1 className="mb-5 text-7xl font-bold">
              {formatCurrency(shippingCost)}
            </h1>
            <h3>Estimated Cost</h3>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-col gap-4">
        {shippingCost === null && (
          <Button
            buttonType="submit"
            className="h-[5.5rem] !rounded-[2rem] text-[2.1rem] font-semibold"
            text="Get Estimate"
            type="secondary"
            loading={loading}
          />
        )}
        <div className="font-semibold text-brand-secondary">
          <Button
            text={shippingCost === null ? 'Cancel' : 'Close'}
            type="text"
            width="fluid"
            className="text-[1.8rem]"
            onClick={handleClose}
          />
        </div>
      </div>
    </form>
  );
}
